import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Phone, MessageCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, telLink, waLink } from "@/lib/api";
import { useAuth, startLogin } from "@/lib/auth";
import { useT } from "@/lib/i18n";

export const PENDING_KEY = "m360_pending_contact";

export const openContact = (b, type, text) => {
  window.location.href = type === "call" ? telLink(b.phone) : waLink(b.whatsapp, text);
};

const useWaText = () => {
  const { t } = useT();
  return (product) => (product ? t("wa_item_msg", { item: product.name }) : t("wa_msg"));
};

const BTN = {
  call: "bg-sky-600 hover:bg-sky-700",
  whatsapp: "bg-emerald-600 hover:bg-emerald-700",
};

export const useContactFlow = (business, returnPath) => {
  const { t, lp } = useT();
  const { user } = useAuth();
  const waText = useWaText();
  const [pending, setPending] = useState(null);

  const logAndOpen = async (type, product) => {
    await api.post(`/businesses/${business.id}/enquiry`, { type, product_id: product?.id || null }).catch(() => {});
    openContact(business, type, type === "whatsapp" ? waText(product) : undefined);
  };

  const start = (type, product = null) => (user ? logAndOpen(type, product) : setPending({ type, product }));

  const google = () => {
    const product = pending.product ? { id: pending.product.id, name: pending.product.name } : null;
    localStorage.setItem(PENDING_KEY, JSON.stringify({ bid: business.id, type: pending.type, product }));
    startLogin(returnPath || lp(`/b/${business.id}`));
  };

  const sheet = (
    <Sheet open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
      <SheetContent side="bottom" className="mx-auto max-w-md rounded-t-3xl bg-paper pb-8" data-testid="contact-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl">{t("contact_title", { name: business.name })}</SheetTitle>
          <SheetDescription>{t("contact_body")}</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-3">
          <button onClick={google} data-testid="contact-sheet-google-btn"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-ink py-3.5 font-semibold text-white active:scale-[0.98]">
            <GoogleG /> {t("continue_google")}
          </button>
          <button onClick={() => { const p = pending; setPending(null); logAndOpen(p.type, p.product); }} data-testid="contact-sheet-guest-btn"
            className="w-full rounded-xl border border-line bg-white py-3.5 font-semibold text-ink active:scale-[0.98]">
            {t("continue_guest")}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
  return { start, sheet };
};

export const usePendingContact = (b, user) => {
  const [ready, setReady] = useState(null);
  const qc = useQueryClient();
  const done = useRef(false);
  useEffect(() => {
    if (!b || !user || done.current) return;
    const p = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    if (!p || p.bid !== b.id) return;
    done.current = true;
    localStorage.removeItem(PENDING_KEY);
    api.post(`/businesses/${b.id}/enquiry`, { type: p.type, product_id: p.product?.id || null }).then(() => {
      setReady(p);
      qc.invalidateQueries({ queryKey: ["bizme", b.id] });
    });
  }, [b, user, qc]);
  return [ready, setReady];
};

export const PendingDialog = ({ b, pending, onClose }) => {
  const { t } = useT();
  const waText = useWaText();
  const type = pending?.type;
  return (
    <Dialog open={!!pending} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm rounded-2xl bg-paper" data-testid="pending-contact-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("enquiry_saved")}</DialogTitle></DialogHeader>
        <button onClick={() => { onClose(); openContact(b, type, type === "whatsapp" ? waText(pending.product) : undefined); }} data-testid="pending-contact-open-btn"
          className={`flex w-full items-center justify-center gap-2 rounded-xl py-3.5 font-bold text-white ${type === "call" ? "bg-sky-600" : "bg-emerald-600"}`}>
          {type === "call" ? <Phone className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
          {type === "call" ? t("open_call") : t("open_whatsapp")}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default function ContactButtons({ business, variant = "card", returnPath }) {
  const { t } = useT();
  const { start, sheet } = useContactFlow(business, returnPath);
  const big = variant === "bar";

  const btn = (kind, Icon) => (
    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); start(kind); }}
      data-testid={`${variant}-${kind}-btn-${business.id}`}
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl font-bold text-white shadow-sm transition-transform active:scale-95 ${BTN[kind]} ${big ? "py-3.5 text-base" : "py-2.5 text-sm"}`}>
      <Icon className={big ? "h-5 w-5" : "h-4 w-4"} /> {t(kind)}
    </button>
  );

  return (
    <>
      <div className="flex gap-2">{btn("call", Phone)}{business.whatsapp && btn("whatsapp", MessageCircle)}</div>
      {sheet}
    </>
  );
}

export const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);
